import React, { useEffect, useState } from 'react';
import { useLocalContext } from '../../context/LocalContext';
import './FindLove.css';

const usaProfiles = [
  {
    id: 1,
    name: 'Aarohi',
    age: 24,
    city: 'New York, NY',
    lat: 40.7128,
    lon: -74.006,
    bio: 'Coffee dates, sunsets, and long drives.',
    image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=900&h=1200&q=80'
  },
  {
    id: 2,
    name: 'Meher',
    age: 26,
    city: 'Los Angeles, CA',
    lat: 34.0522,
    lon: -118.2437,
    bio: 'Fashion lover, foodie, and weekend explorer.',
    image: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?auto=format&fit=crop&w=900&h=1200&q=80'
  },
  {
    id: 3,
    name: 'Rhea',
    age: 25,
    city: 'Chicago, IL',
    lat: 41.8781,
    lon: -87.6298,
    bio: 'Bookstores, concerts, and meaningful conversations.',
    image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=900&h=1200&q=80'
  },
  {
    id: 4,
    name: 'Ira',
    age: 23,
    city: 'Miami, FL',
    lat: 25.7617,
    lon: -80.1918,
    bio: 'Gym mornings and spontaneous travel plans.',
    image: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?auto=format&fit=crop&w=900&h=1200&q=80'
  }
];

const usaReferenceCities = [
  { name: 'New York, NY', lat: 40.7128, lon: -74.006 },
  { name: 'Los Angeles, CA', lat: 34.0522, lon: -118.2437 },
  { name: 'Chicago, IL', lat: 41.8781, lon: -87.6298 },
  { name: 'Houston, TX', lat: 29.7604, lon: -95.3698 },
  { name: 'Miami, FL', lat: 25.7617, lon: -80.1918 },
  { name: 'Seattle, WA', lat: 47.6062, lon: -122.3321 }
];

const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const FindLove = () => {
  const { isAuthenticated, openSignupModal, openLoginModal, showNotification } = useLocalContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState('');
  const [likes, setLikes] = useState(0);
  const [passes, setPasses] = useState(0);
  const [previewSwipes, setPreviewSwipes] = useState(0);
  const [userCoords, setUserCoords] = useState(null);
  const [locationLabel, setLocationLabel] = useState('Location access not enabled');
  const [isLocating, setIsLocating] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(true);
  const [visitorName, setVisitorName] = useState('');
  const [visitorAge, setVisitorAge] = useState('');
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pointerStartX, setPointerStartX] = useState(0);

  useEffect(() => {
    const savedVisitor = localStorage.getItem('findLoveVisitorProfile');
    if (savedVisitor) {
      try {
        const parsed = JSON.parse(savedVisitor);
        if (parsed?.name && parsed?.age) {
          setVisitorName(parsed.name);
          setVisitorAge(String(parsed.age));
          setShowProfilePopup(false);
        }
      } catch {
        setShowProfilePopup(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!swipeDirection) return undefined;
    const timeout = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % usaProfiles.length);
      setSwipeDirection('');
      setDragOffsetX(0);
      setIsDragging(false);
    }, 260);
    return () => clearTimeout(timeout);
  }, [swipeDirection]);

  const requestLocationAccess = () => {
    if (!navigator.geolocation) {
      showNotification('Geolocation is not supported on this browser.', 'error');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setUserCoords(coords);
        setIsLocating(false);

        const nearestCity = usaReferenceCities.reduce((closest, city) => {
          const currentDistance = getDistanceInKm(coords.lat, coords.lon, city.lat, city.lon);
          if (!closest || currentDistance < closest.distance) {
            return { ...city, distance: currentDistance };
          }
          return closest;
        }, null);

        if (nearestCity) {
          setLocationLabel(`Near ${nearestCity.name}`);
        } else {
          setLocationLabel('USA region detected');
        }

        showNotification('Location permission granted. Showing USA matches near you.', 'success');
      },
      () => {
        setIsLocating(false);
        setLocationLabel('Location permission denied');
        showNotification('Location access denied. Showing default USA locations.', 'warning');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const handleSwipe = (direction) => {
    if (swipeDirection) return;
    if (showProfilePopup) return;

    if (!isAuthenticated && previewSwipes >= 3) {
      showNotification('Preview limit reached. Please log in to continue swiping.', 'info');
      openLoginModal();
      return;
    }

    setSwipeDirection(direction);
    setDragOffsetX(direction === 'right' ? 150 : -150);
    setPreviewSwipes((prev) => prev + 1);

    if (direction === 'right') {
      setLikes((prev) => prev + 1);
      return;
    }
    setPasses((prev) => prev + 1);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    const ageNumber = Number(visitorAge);
    if (!visitorName.trim()) {
      showNotification('Please enter your name.', 'warning');
      return;
    }

    if (!Number.isFinite(ageNumber) || ageNumber < 18) {
      showNotification('Please enter a valid age (18+).', 'warning');
      return;
    }

    localStorage.setItem(
      'findLoveVisitorProfile',
      JSON.stringify({ name: visitorName.trim(), age: ageNumber })
    );
    setShowProfilePopup(false);
    showNotification('Welcome! Start swiping now.', 'success');
  };

  const handleEditProfile = () => {
    setShowProfilePopup(true);
  };

  const handlePointerDown = (e) => {
    if (showProfilePopup || swipeDirection) return;
    setIsDragging(true);
    setPointerStartX(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || swipeDirection) return;
    setDragOffsetX(e.clientX - pointerStartX);
  };

  const handlePointerEnd = () => {
    if (!isDragging || swipeDirection) return;
    setIsDragging(false);

    if (dragOffsetX > 100) {
      handleSwipe('right');
      return;
    }

    if (dragOffsetX < -100) {
      handleSwipe('left');
      return;
    }

    setDragOffsetX(0);
  };

  const currentProfile = usaProfiles[activeIndex];
  const remainingSwipes = Math.max(0, 3 - previewSwipes);

  return (
    <section className="find-love-page">
      <div className="container">
        <header className="find-love-topbar">
          <div className="topbar-copy">
            <h1>Find Love</h1>
            <p>Swipe with drag or buttons to preview nearby matches.</p>
          </div>
          <div className="topbar-location">
            <span>{locationLabel}</span>
            <button type="button" onClick={requestLocationAccess} disabled={isLocating}>
              {isLocating ? 'Detecting...' : 'Use My Location'}
            </button>
          </div>
        </header>

        <div className="find-love-shell">
          <section className="swipe-stage">
            <div className="stage-user">
              <div className="stage-user-id">
                <span className="stage-user-label">Your Profile</span>
                <strong className="stage-user-name">{visitorName || 'Guest User'}</strong>
              </div>
              <div className="stage-user-actions">
                <span className="stage-user-age">{visitorAge || '--'} yrs</span>
                <button type="button" onClick={handleEditProfile}>Edit</button>
              </div>
            </div>

            <div className="swipe-card-stack">
              {usaProfiles.map((profile, index) => {
                const relativeIndex = (index - activeIndex + usaProfiles.length) % usaProfiles.length;
                const isActive = relativeIndex === 0;
                const dragStyle = isActive && !swipeDirection
                  ? { transform: `translateX(${dragOffsetX}px) rotate(${dragOffsetX * 0.06}deg)` }
                  : undefined;
                const classes = [
                  'swipe-card',
                  isActive ? 'active' : 'stacked',
                  isActive && swipeDirection ? `swipe-${swipeDirection}` : ''
                ].join(' ').trim();

                const distanceText = userCoords
                  ? `${Math.round(getDistanceInKm(userCoords.lat, userCoords.lon, profile.lat, profile.lon))} km away`
                  : 'Enable location to see distance';

                return (
                  <article
                    key={profile.id}
                    className={classes}
                    aria-hidden={!isActive}
                    style={dragStyle}
                    onPointerDown={isActive ? handlePointerDown : undefined}
                    onPointerMove={isActive ? handlePointerMove : undefined}
                    onPointerUp={isActive ? handlePointerEnd : undefined}
                    onPointerCancel={isActive ? handlePointerEnd : undefined}
                    onPointerLeave={isActive ? handlePointerEnd : undefined}
                  >
                    <img src={profile.image} alt={`${profile.name}, ${profile.age}`} />
                    <div className="swipe-card-overlay" />
                    {isActive && (
                      <>
                        <div className={`swipe-badge like ${dragOffsetX > 25 ? 'visible' : ''}`}>LIKE</div>
                        <div className={`swipe-badge nope ${dragOffsetX < -25 ? 'visible' : ''}`}>NOPE</div>
                      </>
                    )}
                    <div className="swipe-card-info">
                      <h2>{profile.name}, {profile.age}</h2>
                      <span>{profile.city}</span>
                      <small>{distanceText}</small>
                      <p>{profile.bio}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <aside className="control-panel">
            <div className="panel-card">
              <h3>Now Showing</h3>
              <p>{currentProfile.name}, {currentProfile.age} - {currentProfile.city}</p>
            </div>

            <div className="panel-card">
              <h3>Preview Usage</h3>
              <p>{previewSwipes}/3 swipes used - {remainingSwipes} left</p>
            </div>

            <div className="stats-grid">
              <div>
                <strong>{likes}</strong>
                <span>Likes</span>
              </div>
              <div>
                <strong>{passes}</strong>
                <span>Passes</span>
              </div>
            </div>

            <div className="action-row">
              <button type="button" className="pass-btn" onClick={() => handleSwipe('left')} disabled={showProfilePopup}>
                Pass
              </button>
              <button type="button" className="like-btn" onClick={() => handleSwipe('right')} disabled={showProfilePopup}>
                Like
              </button>
            </div>

            <button type="button" className="btn btn-primary btn-lg join-btn" onClick={openSignupModal}>Create Account</button>
          </aside>
        </div>
      </div>
      {showProfilePopup && (
        <div className="profile-popup-backdrop" role="dialog" aria-modal="true" aria-label="Enter name and age">
          <form className="profile-popup-card" onSubmit={handleProfileSubmit}>
            <h3>Welcome to Find Love</h3>
            <p>Before you start swiping, please enter your name and age.</p>
            <label htmlFor="visitor-name">Name</label>
            <input
              id="visitor-name"
              type="text"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              placeholder="Enter your name"
              autoComplete="name"
            />
            <label htmlFor="visitor-age">Age</label>
            <input
              id="visitor-age"
              type="number"
              min="18"
              max="99"
              value={visitorAge}
              onChange={(e) => setVisitorAge(e.target.value)}
              placeholder="Enter your age"
            />
            <button type="submit" className="btn btn-primary btn-lg">
              Start Swiping
            </button>
          </form>
        </div>
      )}
    </section>
  );
};

export default FindLove;
